/**
 * useDebugMode.js - Hook pour détecter le mode debug
 * 
 * Détecte si l'URL contient #ratata pour activer les outils de debug
 * 
 * UTILISATION :
 * - Sans #ratata : Leva est masqué (mode production)
 * - Avec #ratata : Leva est visible (mode développement)
 * 
 * EXEMPLE :
 * https://votre-site.com/        → Leva masqué
 * https://votre-site.com/#ratata  → Leva visible
 * 
 * @returns {boolean} true si mode debug activé
 */

import { useState, useEffect } from 'react'

/**
 * Hook qui retourne true si l'URL contient #ratata
 * 
 * Le hook écoute aussi les changements de hash pour réagir en temps réel
 * si l'utilisateur ajoute ou retire #ratata de l'URL
 * 
 * @returns {boolean} true si mode debug activé, false sinon
 * 
 * @example
 * function MyComponent() {
 *   const isDebug = useDebugMode()
 *   
 *   return (
 *     <>
 *       {isDebug && <DebugPanel />}
 *       <MainContent />
 *     </>
 *   )
 * }
 */
export function useDebugMode() {
  // État pour stocker si le mode debug est actif
  const [isDebugMode, setIsDebugMode] = useState(false)

  useEffect(() => {
    // Fonction pour vérifier le hash
    const checkDebugMode = () => {
      // Vérifie si l'URL contient #ratata
      const hasDebugHash = window.location.hash === '#ratata'
      setIsDebugMode(hasDebugHash)
      
      // Log pour faciliter le debug (ironique, non ? 😄)
      if (hasDebugHash) {
        console.log('🐛 Mode debug activé via #ratata')
      }
    }

    // Vérifier au montage du composant
    checkDebugMode()

    // Écouter les changements de hash (si l'utilisateur modifie l'URL)
    // Cela permet d'activer/désactiver le debug sans recharger la page
    window.addEventListener('hashchange', checkDebugMode)

    // Nettoyage : retirer l'écouteur quand le composant se démonte
    return () => {
      window.removeEventListener('hashchange', checkDebugMode)
    }
  }, [])

  return isDebugMode
}

